// src/context/LocaleContext.js
import React, { createContext, useState, useEffect, useMemo, useCallback } from 'react'; // *** Added useCallback ***
import en from '../locales/en';
import es from '../locales/es';

// --- Configuration ---
const languages = { en, es };
const defaultLanguage = 'en';
const localStorageKey = 'appLanguage';
// --- End Configuration ---

// Create the context object
export const LocaleContext = createContext();

// Create the Provider component
export const LocaleProvider = ({ children }) => {
    // State for the current language code (Your logic is great)
    const [language, setLanguage] = useState(() => {
        try {
            const storedLang = localStorage.getItem(localStorageKey);
            if (storedLang && languages[storedLang]) {
                return storedLang;
            }
        } catch (error) {
            console.error("Error reading language from localStorage", error);
        }
        return defaultLanguage;
    });

    // Effect Hook to save language (Your logic is great)
    useEffect(() => {
        try {
            localStorage.setItem(localStorageKey, language);
        } catch (error) {
            console.error("Error saving language to localStorage", error);
        }
    }, [language]);

    // Function to change the language (Your logic is great)
    // Wrapped in useCallback for stable reference
    const changeLanguage = useCallback((langCode) => {
        if (languages[langCode]) {
            setLanguage(langCode);
        } else {
            console.warn(`Attempted to set invalid language: ${langCode}`);
            setLanguage(defaultLanguage);
        }
    }, []); // No dependencies needed here if it only calls setLanguage

    // Get the correct strings object (Your logic is great)
    const strings = useMemo(() => languages[language] || languages[defaultLanguage], [language]);

    // --- START: Define the 't' translation function ---
    // We use useCallback so components consuming this function don't trigger
    // unnecessary re-renders if the function reference changes without need.
    const t = useCallback((key, fallbackValue = '') => {
        // Get the string from the currently loaded 'strings' object
        const translatedString = strings[key];

        if (translatedString === undefined) {
            // Only log warning in development environment
            if (process.env.NODE_ENV !== 'production') {
                console.warn(`[LocaleProvider] Missing translation key: "${key}" for locale "${language}". Using fallback.`);
            }
            // Return the provided fallback, OR the key itself as an indicator
            return fallbackValue || `[${key}]`;
        }

        // TODO: Add interpolation logic here later if needed (e.g., replace {{variable}})
        // For now, just return the found string
        return translatedString;

    }, [strings, language]); // Dependencies: Recreate 't' if strings object or language changes
    // --- END: Define the 't' translation function ---


    // --- UPDATE: Add 't' to the context value ---
    // The value object provided to consuming components
    // Wrapped in useMemo so consumers only re-render when these specific values change
    const contextValue = useMemo(() => ({
        language,
        strings, // Keep providing raw strings for now, makes migration easier
        changeLanguage,
        handleLanguageChange: changeLanguage, // Keep alias if used
        t: t // Add the new translation function
    }), [language, strings, changeLanguage, t]); // Add 't' to dependency array
    // --- END UPDATE ---

    return (
        <LocaleContext.Provider value={contextValue}>
            {children}
        </LocaleContext.Provider>
    );
};